import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Brain, Check, CheckCircle2, Loader2, ShieldCheck, Sparkles, Trophy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";
import {
    quizQuestions,
    scoreQuiz,
    profileFromScores,
    recommendations,
} from "@/components/quiz/quizData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = {
    INTRO: "intro",
    QUESTIONS: "questions",
    EMAIL: "email",
    RESULT: "result",
};

export default function Test() {
    const [step, setStep] = useState(STEPS.INTRO);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ parent_name: "", child_first_name: "", email: "" });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [step]);

    const total = quizQuestions.length;
    const q = quizQuestions[current];
    const progress = Math.round(((current + (answers[q?.id] !== undefined ? 1 : 0)) / total) * 100);

    const handleSelect = (idx) => {
        setAnswers((prev) => ({ ...prev, [q.id]: idx }));
        // small delay then advance
        setTimeout(() => {
            if (current < total - 1) {
                setCurrent((c) => c + 1);
            } else {
                setStep(STEPS.EMAIL);
            }
        }, 220);
    };

    const handleBack = () => {
        if (current > 0) setCurrent((c) => c - 1);
        else setStep(STEPS.INTRO);
    };

    const score = useMemo(() => scoreQuiz(answers), [answers]);
    const profile = useMemo(() => profileFromScores(score), [score]);
    const recs = useMemo(() => recommendations(score), [score]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!contact.parent_name.trim() || !contact.email.trim()) {
            toast.error("Merci d'indiquer votre prénom et votre email.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                parent_name: contact.parent_name.trim(),
                child_first_name: contact.child_first_name.trim() || null,
                email: contact.email.trim(),
                answers,
                scores: score,
                profile_slug: profile.slug,
            };
            const { data } = await axios.post(`${API}/test/submit`, payload);
            setResult(data);
            setStep(STEPS.RESULT);
        } catch (err) {
            toast.error("Erreur lors de l'envoi. Merci de réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink-50/30">
            <header className="border-b border-ink-100 bg-white/85 backdrop-blur-xl sticky top-0 z-40">
                <div className="container-wide flex items-center justify-between h-24">
                    <Logo />
                    <Link to="/" className="btn-ghost text-sm" data-testid="test-back-home">
                        Retour au site
                    </Link>
                </div>
            </header>

            <main className="container-narrow py-14 sm:py-20">
                {step === STEPS.INTRO && <Intro onStart={() => setStep(STEPS.QUESTIONS)} />}

                {step === STEPS.QUESTIONS && (
                    <Question
                        q={q}
                        index={current}
                        total={total}
                        selected={answers[q?.id]}
                        progress={progress}
                        onSelect={handleSelect}
                        onBack={handleBack}
                    />
                )}

                {step === STEPS.EMAIL && (
                    <EmailGate
                        contact={contact}
                        setContact={setContact}
                        submitting={submitting}
                        onSubmit={submit}
                        onBack={() => {
                            setStep(STEPS.QUESTIONS);
                            setCurrent(total - 1);
                        }}
                    />
                )}

                {step === STEPS.RESULT && result && (
                    <Result
                        profile={profile}
                        score={score}
                        recs={recs}
                        emailSent={result?.email_sent}
                        contact={contact}
                        onBookDiagnostic={() => navigate("/diagnostic")}
                    />
                )}
            </main>
        </div>
    );
}

function Intro({ onStart }) {
    return (
        <div data-testid="quiz-intro" className="text-center max-w-2xl mx-auto animate-fade-up">
            <span className="eyebrow-pink">Test gratuit · 3 minutes</span>
            <h1 className="display-1 mt-5">
                Découvrez le <span className="text-brand-500">profil neuro-éducatif</span> de votre enfant.
            </h1>
            <p className="body-lg mt-6">
                10 questions rapides pour analyser sa concentration, ses émotions, son organisation — et
                recevez un plan d'action personnalisé basé sur les neurosciences, le stoïcisme et le Système
                Familial Haute Performance™.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
                {[
                    { icon: Brain, label: "10 questions" },
                    { icon: Sparkles, label: "Résultats immédiats" },
                    { icon: ShieldCheck, label: "100% confidentiel" },
                ].map(({ icon: Icon, label }) => (
                    <div
                        key={label}
                        className="soft-card flex items-center justify-center gap-3 py-4"
                    >
                        <Icon className="h-4 w-4 text-brand-600" strokeWidth={2} />
                        <span className="text-sm font-medium text-ink-700">{label}</span>
                    </div>
                ))}
            </div>

            <button
                onClick={onStart}
                data-testid="quiz-start-btn"
                className="btn-primary mt-10 text-base"
            >
                Commencer le test
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
            </button>
            <p className="mt-4 text-xs text-ink-400">Sans inscription préalable. Aucun spam.</p>
        </div>
    );
}

function Question({ q, index, total, selected, progress, onSelect, onBack }) {
    if (!q) return null;
    return (
        <div data-testid="quiz-question" className="animate-fade-up">
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 transition-colors"
                    data-testid="quiz-back-btn"
                >
                    <ArrowLeft className="h-4 w-4" strokeWidth={2} /> Retour
                </button>
                <span className="text-sm text-ink-500 font-medium">
                    Question{" "}
                    <span className="text-ink-900 font-semibold">
                        {index + 1}
                    </span>{" "}
                    / {total}
                </span>
            </div>
            <Progress value={progress} className="h-1.5 bg-ink-100 [&>div]:bg-brand-500" data-testid="quiz-progress" />

            <div className="mt-12 max-w-2xl">
                <span className="eyebrow-brand">{q.category}</span>
                <h2 className="display-2 mt-3" data-testid="quiz-question-title">
                    {q.question}
                </h2>
            </div>

            <ul className="mt-10 grid gap-3" data-testid="quiz-options">
                {q.options.map((opt, i) => {
                    const isSelected = selected === i;
                    return (
                        <li key={i}>
                            <button
                                type="button"
                                onClick={() => onSelect(i)}
                                data-testid={`quiz-option-${i}`}
                                className={`w-full text-left rounded-2xl border-2 px-5 sm:px-6 py-5 transition-all flex items-center gap-4 hover:-translate-y-0.5 ${
                                    isSelected
                                        ? "border-brand-500 bg-brand-50 shadow-[0_18px_40px_-25px_rgba(20,184,166,0.6)]"
                                        : "border-ink-200 bg-white hover:border-brand-300"
                                }`}
                            >
                                <span
                                    className={`h-9 w-9 rounded-full grid place-items-center font-semibold text-sm shrink-0 transition-colors ${
                                        isSelected
                                            ? "bg-brand-500 text-white"
                                            : "bg-ink-100 text-ink-600"
                                    }`}
                                >
                                    {isSelected ? <Check className="h-4 w-4" strokeWidth={2.5} /> : String.fromCharCode(65 + i)}
                                </span>
                                <span className="text-base font-medium text-ink-800 flex-1">
                                    {opt.label}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function EmailGate({ contact, setContact, submitting, onSubmit, onBack }) {
    return (
        <form onSubmit={onSubmit} data-testid="quiz-email-gate" className="max-w-xl mx-auto animate-fade-up">
            <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 transition-colors"
                data-testid="quiz-emailgate-back"
            >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} /> Modifier mes réponses
            </button>
            <div className="mt-8 text-center">
                <span className="eyebrow-pink">Dernière étape</span>
                <h2 className="display-2 mt-3">Recevez votre profil détaillé.</h2>
                <p className="body-lg mt-5">
                    Vos résultats apparaissent immédiatement à l'écran. Nous vous envoyons aussi un email
                    récapitulatif avec votre plan d'action — vous pourrez le relire à tête reposée.
                </p>
            </div>

            <div className="soft-card p-7 sm:p-9 mt-10 space-y-5">
                <div>
                    <Label htmlFor="parent_name" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                        Votre prénom
                    </Label>
                    <Input
                        id="parent_name"
                        required
                        data-testid="quiz-parent-name"
                        value={contact.parent_name}
                        onChange={(e) => setContact({ ...contact, parent_name: e.target.value })}
                        className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                        placeholder="Alex"
                    />
                </div>
                <div>
                    <Label htmlFor="child_first_name" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                        Prénom de l'enfant <span className="lowercase tracking-normal text-ink-400">(optionnel)</span>
                    </Label>
                    <Input
                        id="child_first_name"
                        data-testid="quiz-child-name"
                        value={contact.child_first_name}
                        onChange={(e) => setContact({ ...contact, child_first_name: e.target.value })}
                        className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                        placeholder="Léo"
                    />
                </div>
                <div>
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                        Email
                    </Label>
                    <Input
                        id="email"
                        type="email"
                        required
                        data-testid="quiz-email"
                        value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                        placeholder="alex@exemple.fr"
                    />
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quiz-submit"
                    className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse en cours...
                        </>
                    ) : (
                        <>
                            Découvrir mes résultats
                            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
                        </>
                    )}
                </button>
                <p className="text-xs text-ink-400 text-center">
                    Nous respectons votre vie privée. Aucun partage à des tiers.
                </p>
            </div>
        </form>
    );
}

function Result({ profile, score, recs, emailSent, contact, onBookDiagnostic }) {
    const dimensions = [
        { key: "neuro", label: "Neurosciences", color: "bg-brand-500" },
        { key: "stoic", label: "Stoïcisme", color: "bg-pinky-500" },
        { key: "system", label: "Système Familial™", color: "bg-ink-800" },
    ];

    return (
        <div data-testid="quiz-result" className="animate-fade-up">
            <div className="text-center max-w-2xl mx-auto">
                <span className={`eyebrow ${profile.tone === "pink" ? "text-pinky-500" : "text-brand-600"}`}>
                    Profil détecté
                </span>
                <h1 className="display-1 mt-4">{profile.title}</h1>
                <p className="body-lg mt-5">{profile.headline}</p>
            </div>

            {/* Score panel */}
            <div className="soft-card p-7 sm:p-10 mt-12">
                <div className="grid sm:grid-cols-3 gap-8">
                    {dimensions.map((d) => (
                        <div key={d.key} data-testid={`score-${d.key}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-ink-700">{d.label}</span>
                                <span className="text-sm font-mono text-ink-500">{score.pct[d.key]}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-ink-100 mt-2 overflow-hidden">
                                <div
                                    className={`h-full ${d.color} rounded-full transition-all`}
                                    style={{ width: `${score.pct[d.key]}%` }}
                                />
                            </div>
                            <p className="text-xs text-ink-500 mt-2">
                                {score.pct[d.key] >= 50
                                    ? "Levier prioritaire"
                                    : score.pct[d.key] >= 25
                                        ? "Marges de progression"
                                        : "Très bon socle"}
                            </p>
                        </div>
                    ))}
                </div>
                <p className="mt-8 text-ink-700 leading-relaxed">{profile.description}</p>
            </div>

            {/* Recommendations */}
            <div className="mt-14">
                <div className="text-center max-w-xl mx-auto">
                    <span className="eyebrow-brand">Votre plan d'action</span>
                    <h2 className="display-2 mt-3">Vos 3 prochaines actions.</h2>
                </div>
                <div className="mt-10 grid md:grid-cols-3 gap-5">
                    {recs.map((r, i) => (
                        <article
                            key={i}
                            data-testid={`recommendation-${i}`}
                            className="soft-card p-7 transition-all hover:-translate-y-1"
                        >
                            <span
                                className={`inline-block text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${
                                    r.tag === "Stoïcisme"
                                        ? "bg-pinky-50 text-pinky-600"
                                        : r.tag === "Organisation"
                                            ? "bg-ink-100 text-ink-800"
                                            : "bg-brand-50 text-brand-700"
                                }`}
                            >
                                {r.tag}
                            </span>
                            <h3 className="font-display font-semibold text-lg text-ink-900 mt-4 leading-snug">
                                {r.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-ink-600">{r.body}</p>
                        </article>
                    ))}
                </div>
            </div>

            {/* Final CTA */}
            <div className="mt-16 rounded-[2rem] bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 text-white px-8 sm:px-14 py-14 sm:py-16 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 h-[260px] w-[260px] rounded-full bg-white/10 blur-3xl" />
                <div className="relative max-w-2xl mx-auto text-center">
                    <Trophy className="h-8 w-8 mx-auto text-brand-100" strokeWidth={2} />
                    <h2 className="display-2 mt-4 !text-white">
                        Allez plus loin avec un diagnostic offert.
                    </h2>
                    <p className="mt-4 text-brand-50/90 text-base sm:text-lg">
                        30 minutes en visio avec Peggy ou Didier pour traduire ces résultats en plan d'action
                        sur-mesure pour {contact.child_first_name || "votre enfant"}.
                    </p>
                    <button
                        onClick={onBookDiagnostic}
                        data-testid="result-book-diagnostic"
                        className="mt-8 inline-flex items-center justify-center rounded-full bg-white text-brand-700 font-semibold px-7 py-3.5 shadow-lg hover:-translate-y-0.5 transition-all"
                    >
                        Réserver mon diagnostic offert
                        <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>
            </div>

            {emailSent && (
                <p
                    data-testid="result-email-sent"
                    className="mt-8 flex items-center justify-center gap-2 text-sm text-brand-700"
                >
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                    Récapitulatif envoyé à {contact.email}.
                </p>
            )}
        </div>
    );
}
